// KPIS-MODE: the twin's scorecards at the month and quarter grain (`period_type=month|quarter`,
// keys `2026-10` / `2026-Q4`), the way the API serves them: the period's card with a 13-period
// trend of the same grain. The twin's numbers are weekly (fake-data, fake-structure), so a month
// or a quarter is composed from the week cards: the weeks whose Thursday falls in the period
// (ISO convention), each metric's weekly actuals and targets summed (numbers, currency, time) or
// averaged (percent, rating, yes/no) — the company's own rows and every child's. The card's
// subject, children, scores, cost and hours are the period's last week's (a twin's
// simplification: the API scores the period itself).
import { attainment, DEFAULTS, metricHealth } from "@companyos/domain-kpi";
import { weekStart } from "../focus/weeks";
import type { Scorecard, ScorecardMetric } from "./types";

export type Grain = "week" | "month" | "quarter";
export type PeriodParam = { type: Grain; key: string };

const WEEK = /^\d{4}-W\d{2}$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const QUARTER = /^\d{4}-Q[1-4]$/;
/** The trend's length at every grain (the API's `TREND_LENGTH`). */
export const GRAIN_TREND = 13;

const DAY = 86_400_000;
const thursday = (week: string) => new Date(weekStart(week).getTime() + 3 * DAY);
const monthOf = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
const quarterKeyOf = (d: Date) => `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
const keyOf = (type: "month" | "quarter", week: string) =>
  type === "month" ? monthOf(thursday(week)) : quarterKeyOf(thursday(week));

/** The period's first day and the first day after it (UTC). */
function bounds(type: "month" | "quarter", key: string): [Date, Date] {
  const year = Number(key.slice(0, 4));
  const month = type === "month" ? Number(key.slice(5, 7)) - 1 : (Number(key.slice(6)) - 1) * 3;
  const span = type === "month" ? 1 : 3;
  return [new Date(Date.UTC(year, month, 1)), new Date(Date.UTC(year, month + span, 1))];
}

/** The period `delta` periods away. */
function shiftPeriod(type: "month" | "quarter", key: string, delta: number): string {
  const [start] = bounds(type, key);
  const d = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + delta * (type === "month" ? 1 : 3), 1),
  );
  return type === "month" ? monthOf(d) : quarterKeyOf(d);
}

/** The ISO weeks of a period: the weeks whose Thursday falls in it, oldest first. */
export function weeksOfPeriod(type: "month" | "quarter", key: string): string[] {
  const [start, end] = bounds(type, key);
  // The Thursday on or after the first day, then every 7 days while inside the period.
  const first = new Date(start.getTime() + ((4 - start.getUTCDay() + 7) % 7) * DAY);
  const out: string[] = [];
  for (let t = first.getTime(); t < end.getTime(); t += 7 * DAY) {
    const d = new Date(t);
    // ISO week of a Thursday: its year and the week number counted from that year's first Thursday.
    const jan1 = Date.UTC(d.getUTCFullYear(), 0, 1);
    const n = Math.ceil(((t - jan1) / DAY + 1) / 7);
    out.push(`${d.getUTCFullYear()}-W${String(n).padStart(2, "0")}`);
  }
  return out;
}

/**
 * `period_type` and `period` of a scorecard read: a week (the default), a month or a quarter.
 * `defaultWeek` is the current week (the API's default period when none is given). A bad pair
 * is `{ error }`, the handler's 422 detail.
 */
export function periodParam(url: URL, defaultWeek: () => string): PeriodParam | { error: string } {
  const type = url.searchParams.get("period_type") ?? "week";
  const key = url.searchParams.get("period");
  const bad = (error: string) => ({ error });
  if (type === "week") {
    const week = key ?? defaultWeek();
    return WEEK.test(week) ? { type, key: week } : bad("period must be a week key");
  }
  if (type === "month") {
    const month = key ?? keyOf("month", defaultWeek());
    return MONTH.test(month) ? { type, key: month } : bad("period must be a month key");
  }
  if (type === "quarter") {
    const quarter = key ?? keyOf("quarter", defaultWeek());
    return QUARTER.test(quarter) ? { type, key: quarter } : bad("period must be a quarter key");
  }
  return bad(`period_type=${type} is not served by the fake`);
}

/** Sum or average, as the unit reads (a fraction or a rating averages; counts add). */
const additive = (unit: ScorecardMetric["unit"]) =>
  unit === "number" || unit === "currency" || unit === "time";
function combine(values: (number | null)[], unit: ScorecardMetric["unit"]): number | null {
  const present = values.filter((v): v is number => v !== null);
  if (!present.length) return null;
  const sum = present.reduce((s, v) => s + v, 0);
  return additive(unit)
    ? Math.round(sum * 100) / 100
    : Math.round((sum / present.length) * 1000) / 1000;
}

type Point = { actual: number | null; target: number | null };

/**
 * The card for a period at any grain: a week is `cardAt(week)` itself; a month or a quarter is
 * composed from the week cards (see the header). `cardAt` returns null when the subject has no
 * card (the handler's 404).
 */
export function cardForPeriod(
  p: PeriodParam,
  cardAt: (week: string) => Scorecard | null,
): Scorecard | null {
  if (p.type === "week") return cardAt(p.key);
  const type = p.type;
  const memo = new Map<string, Scorecard | null>();
  const at = (week: string) => {
    if (!memo.has(week)) memo.set(week, cardAt(week));
    return memo.get(week) ?? null;
  };
  const periods = Array.from({ length: GRAIN_TREND }, (_, i) =>
    shiftPeriod(type, p.key, i - (GRAIN_TREND - 1)),
  );
  const weeksOf = new Map(periods.map((k) => [k, weeksOfPeriod(type, k)]));
  const lastWeek = (k: string) => (weeksOf.get(k) ?? []).at(-1) ?? "";
  const main = at(lastWeek(p.key));
  if (!main) return null;

  // Every week's values per (row, metric), read from the trends of anchor cards that cover the
  // period's weeks (a week card's trend is its last 13 weeks).
  const values = new Map<string, Map<string, Point>>();
  const take = (row: string, m: ScorecardMetric) => {
    const k = `${row}|${m.metric_def_id}`;
    const byWeek = values.get(k) ?? new Map<string, Point>();
    values.set(k, byWeek);
    for (const t of m.trend) byWeek.set(t.period_key, { actual: t.actual, target: t.target });
  };
  for (const k of periods) {
    const weeks = weeksOf.get(k) ?? [];
    const anchors = new Set<string>([lastWeek(k)]);
    for (let i = weeks.length - 1 - GRAIN_TREND; i >= 0; i -= GRAIN_TREND)
      anchors.add(weeks[i] as string);
    for (const w of anchors) {
      const card = at(w);
      if (!card) continue;
      for (const m of card.metrics) take("self", m);
      for (const c of card.children ?? []) for (const m of c.metrics ?? []) take(c.subject.id, m);
    }
  }

  const regrain = (row: string, m: ScorecardMetric): ScorecardMetric => {
    const byWeek = values.get(`${row}|${m.metric_def_id}`) ?? new Map<string, Point>();
    const point = (k: string) => {
      const pts = (weeksOf.get(k) ?? []).map(
        (w) => byWeek.get(w) ?? { actual: null, target: null },
      );
      return {
        period_key: k,
        actual: combine(
          pts.map((x) => x.actual),
          m.unit,
        ),
        target: combine(
          pts.map((x) => x.target),
          m.unit,
        ),
      };
    };
    const trend = periods.map(point);
    const now = trend.at(-1) as (typeof trend)[number];
    const att =
      now.actual === null || now.target === null || m.tracking_mode !== "scored"
        ? null
        : Math.round(
            attainment(m.direction, now.actual, now.target, DEFAULTS.attainment_cap, null) * 1000,
          ) / 1000;
    return {
      ...m,
      actual: now.actual,
      target: now.target,
      attainment: att,
      health: metricHealth(att, m.bands),
      state: now.actual === null && m.tracking_mode !== "context" ? "missing" : m.state,
      trend,
    };
  };

  const [start, end] = bounds(type, p.key);
  return {
    ...main,
    period: {
      type,
      key: p.key,
      starts_at: start.toISOString(),
      ends_at: `${new Date(end.getTime() - 1000).toISOString().slice(0, 19)}Z`,
    },
    metrics: main.metrics.map((m) => regrain("self", m)),
    ...(main.children
      ? {
          children: main.children.map((c) =>
            c.metrics ? { ...c, metrics: c.metrics.map((m) => regrain(c.subject.id, m)) } : c,
          ),
        }
      : {}),
  };
}
